import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../components/Logo";
import OtpInput from "../../components/OtpInput";
import { usePatientAuth } from "../../store/authPatientStore";

const PENDING_OTP_KEY = "agbaza.patient.signup.otpRequestId";
const PENDING_EMAIL_KEY = "agbaza.patient.signup.email";

type SignupCodeState = {
  otpRequestId?: string;
  email?: string;
};

export default function PatientSignupCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmLoginOtp } = usePatientAuth();

  const state = (location.state as SignupCodeState | null) ?? null;

  const [otpRequestId, setOtpRequestId] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fromState = state?.otpRequestId ?? "";
    const fromStorage = sessionStorage.getItem(PENDING_OTP_KEY) ?? "";
    const resolved = fromState || fromStorage;

    if (!resolved) {
      navigate("/patient/inscription", { replace: true });
      return;
    }

    setOtpRequestId(resolved);
    if (!fromStorage && fromState) sessionStorage.setItem(PENDING_OTP_KEY, fromState);

    const emailFromState = state?.email ?? "";
    const emailFromStorage = sessionStorage.getItem(PENDING_EMAIL_KEY) ?? "";
    const resolvedEmail = emailFromState || emailFromStorage;
    setEmail(resolvedEmail);
    if (!emailFromStorage && emailFromState) sessionStorage.setItem(PENDING_EMAIL_KEY, emailFromState);
  }, [navigate, state?.otpRequestId, state?.email]);

  const codeValue = code.replace(/\s/g, "");
  const ready = codeValue.length === 6 && !!otpRequestId && !submitting;
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (ready && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      onConfirm();
    }
    if (!ready) autoSubmittedRef.current = false;
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirm = async () => {
    if (!ready || !otpRequestId) return;
    setError("");
    setSubmitting(true);
    const result = await confirmLoginOtp(otpRequestId, codeValue);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message || "Code invalide ou expiré.");
      autoSubmittedRef.current = false;
      return;
    }

    sessionStorage.removeItem(PENDING_OTP_KEY);
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
    navigate("/patient", { replace: true, state: { showIdentityBanner: true } });
  };

  const onBack = () => {
    sessionStorage.removeItem(PENDING_OTP_KEY);
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
  };

  if (!otpRequestId) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0B1F16]">
      <main className="flex-1 w-full max-w-sm mx-auto flex flex-col items-center justify-center py-8 px-4">
        <div className="text-center">
          <Logo size={90} />
          <p className="mt-4 text-lg font-semibold text-[#0B1F16]">Vérifiez votre email</p>
          <p className="mt-1 text-sm text-gray-600">
            {email
              ? `Un code à 6 chiffres a été envoyé à ${email}.`
              : "Saisissez le code reçu par email pour confirmer votre inscription."}
          </p>
        </div>

        {error && (
          <div className="mt-4 w-full text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
            {error}
          </div>
        )}

        <div className="mt-6 w-full">
          <OtpInput value={code} onChange={setCode} length={6} disabled={submitting} />
        </div>

        <div className="mt-6 w-full space-y-3">
          <button
            type="button"
            className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
            style={{
              backgroundColor: ready ? "#00553E" : "#DCEFE9",
              color: ready ? "#FFFFFF" : "#7aa79a",
            }}
            disabled={!ready}
            onClick={onConfirm}
          >
            {submitting ? "Vérification..." : "Confirmer l'inscription"}
          </button>
          <Link to="/patient/inscription" className="block" onClick={onBack}>
            <button
              type="button"
              className="w-full h-12 rounded-[10px] font-semibold border cursor-pointer"
              style={{ borderColor: "#D1E2DD", color: "#00553E" }}
            >
              Retour
            </button>
          </Link>
        </div>
      </main>
    </div>
  );
}
